import * as CL from '@design-system/component-library';
import { useEffect, useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import {
  corporateCustomersMsg,
  noDeviceAvailableMsg,
  orStartingAtMonthlyMsg,
  shopMsg,
  showMoreMsg,
  t,
} from '../../common/i18n/index.js';
import { eurosToCents, formatSum } from '../../common/utils/priceUtils.js';
import { getLocalizedString, getProductOfferingPriceDataFromType } from '../../common/utils/productOfferingsUtils.js';
import { BreadCrumbs } from '../BreadCrumbs/index.js';
import { Grid } from '../Grid/Grid.js';
import { PriceType } from '../HorizontalSubscriptionCard/types';
import { OfferBadge } from '../OfferBadge/OfferBadge.js';
import { ProductGridHeaderCoCo } from './ProductGridHeaderCoCo.js';
import { gridSearch } from './ProductGridSearchCoCo.js';
import { SortType, sortProducts } from './ProductGridSortCoCo.js';
import { ProductGridTitleCoCo } from './ProductGridTitleCoCo.js';
import { createFilters, gridFilter, rebuildFilters } from './productGridFilterUtilsCoCo.js';
import {
  PRODUCT_GRID_BADGES,
  getEnergyLabelRating,
  getLowestInstallmentOption,
  getProductGridBadges,
  getProductUrl,
  toProductVariants,
} from './productGridUtilsCoCo.js';
import { useVisibleProducts } from './useVisibleProducts.js';
import type { Filters } from './ProductGridFilterCoCo.js';
import type { DeviceProductOffer, ProductGridBadge } from './productGridUtilsCoCo.js';

import './ProductGridCoCo.scss';

const EMPTY_OFFERS: DeviceProductOffer[] = [];

interface DeviceOfferCardProps {
  offer: DeviceProductOffer;
}

const DeviceOfferCard = ({ offer }: DeviceOfferCardProps) => {
  const name = getLocalizedString(offer.name);
  const price = getProductOfferingPriceDataFromType(offer.productOfferingPrice, PriceType.ONE_TIME_CHARGE);
  const lowestInstallment = price ? getLowestInstallmentOption(price) : undefined;
  const productVariants = toProductVariants(offer);
  const badges = getProductGridBadges(offer);
  const energyLabelRating = getEnergyLabelRating(offer);

  const createBadge = (badge: ProductGridBadge) => {
    switch (badge) {
      case PRODUCT_GRID_BADGES.FIVE_G:
        return <CL.Badge key="5g" text="5G" color="5g" />;
      case PRODUCT_GRID_BADGES.REFURBISHED:
        return <CL.Badge key="refurbished" text="Refurbished" color="neutral-subtle" />;
      case PRODUCT_GRID_BADGES.PAID_BY_EMPLOYER:
        return <CL.Badge key="paidByEmployer" text={t.Y9JE('Paid by employer')} color="warning" />;
      case PRODUCT_GRID_BADGES.DISCOUNT:
        return <OfferBadge key="discount" offerBadge={offer.offerBadge} />;
      default:
        return undefined;
    }
  };

  const productBadge = badges.length ? (
    <div className="of-public-device-offers__badges">{badges.map(createBadge)}</div>
  ) : undefined;

  return (
    <CL.ProductCard
      productName={name}
      productImage={
        // TODO: update the image url once we have Contentful integration
        <CL.Picture
          src="https://static.elisa.com/v2/image/2tqybbhjs47b/4dSpRbc7qr4u3mqra2z9ot/Apple-iPhone-15-Plus-Blue-combo.png?w=895&fm=avif"
          i18n_picture_altText="Alt text"
        />
      }
      productPrice={
        <CL.Price
          currentPrice={price?.priceWithoutTax ? price?.priceWithoutTax.toString() : ''}
          currentPriceUnit="€"
          additionalInfo={
            lowestInstallment?.priceWithoutTax === undefined
              ? undefined
              : t.OD1M(orStartingAtMonthlyMsg, formatSum(eurosToCents(lowestInstallment.priceWithoutTax), true)!)
          }
          energyLabel={
            energyLabelRating ? (
              <CL.EnergyLabel rating={energyLabelRating} size="s" fluidSize i18n_energyLabel_label="" />
            ) : undefined
          }
        />
      }
      productVariants={productVariants}
      productURL={getProductUrl(offer)}
      productBadge={productBadge}
    />
  );
};

interface ProductGridCoCoProps {
  offers?: DeviceProductOffer[];
}

export const ProductGridCoCo = ({ offers: offersProp }: ProductGridCoCoProps = {}) => {
  const loaderOffers = useLoaderData() as DeviceProductOffer[] | undefined;
  const offers = offersProp ?? loaderOffers ?? EMPTY_OFFERS;

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortType>(SortType.ORDER_NEWEST);
  const [filters, setFilters] = useState<Filters>(() => createFilters(offers));

  // Rebuild filter options (brand/price ranges) whenever the offers from backend change,
  // preserving any already-selected options.
  useEffect(() => {
    setFilters(previous => rebuildFilters(offers, previous));
  }, [offers]);

  const filteredOffers = useMemo(
    () => offers.filter(gridSearch(search)).filter(gridFilter(filters)).sort(sortProducts(sort)),
    [offers, search, filters, sort]
  );

  const { visibleOffers, hasMoreOffers, showMore } = useVisibleProducts(filteredOffers);

  return (
    <section className="of-public-device-offers of-public-page" id="public-device-offers">
      <BreadCrumbs breadCrumbPaths={[{ name: t.KBLX(corporateCustomersMsg), path: '/' }, { name: t.BZYZ(shopMsg) }]} />
      <Grid colWidthL={10}>
        <ProductGridTitleCoCo />
        {offers.length === 0 ? (
          <p className="of-public-device-offers__empty">{t.OQDT(noDeviceAvailableMsg)}</p>
        ) : (
          <>
            <ProductGridHeaderCoCo
              filters={filters}
              search={search}
              sort={sort}
              setFilters={setFilters}
              setSearch={setSearch}
              setSort={setSort}
            />
            <CL.ProductGridV2>
              {visibleOffers.map(offer => (
                <DeviceOfferCard key={offer.id} offer={offer} />
              ))}
            </CL.ProductGridV2>
            {hasMoreOffers && (
              <div className="of-public-device-offers__show-more">
                <CL.Button
                  size="l"
                  color="light"
                  className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_4}`}
                  onClick={showMore}
                >
                  {t.H6OA(showMoreMsg)}
                </CL.Button>
              </div>
            )}
          </>
        )}
      </Grid>
    </section>
  );
};
